import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  // Intercept clicks on internal links inside dangerouslySetInnerHTML content
  // to prevent full page reloads and use React Router navigation instead
  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href && href.startsWith('/') && !href.startsWith('//')) {
        e.preventDefault();
        window.REACT_APP_NAVIGATE(href);
      }
    }
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticlesData = articles.filter((a) => article.relatedArticles.includes(a.slug));

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article>
          <header className="bg-background-100 py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <nav className="text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/guide" className="hover:text-primary-600 transition-colors">Shopping Guide</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-700">{article.title.length > 50 ? article.title.substring(0, 50) + '...' : article.title}</span>
              </nav>

              <div className="mb-6">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full aspect-[2/1] object-cover rounded-xl"
                  loading="eager"
                />
              </div>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                {article.metaDescription}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span>By China Buying Research Team</span>
                <span>·</span>
                <span>Published {article.datePublished}</span>
                <span>·</span>
                <span>Updated {article.dateModified}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </header>

          <div
            className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16"
            onClick={handleContentClick}
          >
            <div className="space-y-10 text-sm md:text-base text-foreground-700 leading-relaxed">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.content.map((paragraph, pIdx) => (
                    <p key={pIdx} className="mb-4" dangerouslySetInnerHTML={{
                      __html: paragraph
                        .replace(/AllChinaBuy Spreadsheet/g, '<strong>AllChinaBuy Spreadsheet</strong>')
                        .replace(/AllChinaBuy Sheet/g, '<a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" class="text-primary-600 underline font-medium">AllChinaBuy Sheet</a>')
                        .replace(/Shopping Guide/g, '<a href="/guide" class="text-primary-600 underline font-medium">Shopping Guide</a>'),
                    }}></p>
                  ))}
                </section>
              ))}
            </div>

            {/* Internal link to homepage */}
            <div className="mt-12 p-5 bg-primary-50 border border-primary-100 rounded-xl">
              <p className="text-sm text-foreground-700">
                <strong className="text-primary-700">Continue Exploring:</strong>{' '}
                Return to our{' '}
                <Link to="/" className="text-primary-600 underline font-medium">AllChinaBuy Spreadsheet 2026 homepage</Link>{' '}
                for the latest verified finds, updated seller ratings, and direct product links across all 14 categories. Or visit the{' '}
                <Link to="/guide" className="text-primary-600 underline font-medium">complete Shopping Guide</Link>{' '}
                for step-by-step instructions.
              </p>
            </div>

            {/* Related Articles */}
            {relatedArticlesData.length > 0 && (
              <div className="mt-12">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticlesData.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/articles/${ra.slug}`}
                      className="group block bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden bg-background-100">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-foreground-500 mb-1">{ra.readTime}</p>
                        <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {ra.title}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer justify-center"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer justify-center"
              >
                AllChinaBuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.metaDescription,
        image: article.image,
        author: {
          '@type': 'Organization',
          name: 'China Buying Research Team',
        },
        publisher: {
          '@type': 'Organization',
          name: 'allchinabuy.info',
        },
        datePublished: article.datePublished,
        dateModified: article.dateModified,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/articles/${article.slug}`,
        },
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Shopping Guide',
            item: `${SITE_URL}/guide`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title.substring(0, 60),
            item: `${SITE_URL}/articles/${article.slug}`,
          },
        ],
      }) }} />
    </>
  );
}